import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePreloadWmOfferUrl, useOfferData } from 'cityads-v3/hooks/offers'
import { Offer } from 'cityads-v3/types/offers'
import { RootState } from 'cityads-v3/types/reducer'
import { WmOfferUrl } from 'cityads-v3/types/wmOfferUrls'

import GetLinkButton from './GetLinkButton'
import GetLinkDialog from '../offers/GetLinkDialog';
import NumberFormat, { FORMAT_PERCENT } from 'cityads-v3/components/Format/NumberFormat'
import Table from 'cityads-v3/components/Table/Table'
import { TableColumn } from 'cityads-v3/components/Table/TableTypes'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

export type LandingsListProps = {
  offerId: Offer['id'],
}

export default function LandingsList({ offerId }: LandingsListProps): JSX.Element {
  const { t } = useTranslation(['Offers', 'main'])
  const history = useHistory()
  usePreloadWmOfferUrl(offerId)
  const landings = useSelector((state: RootState) => state.wmOfferUrls.urlsByOfferId[offerId]?.dataById ?? {})
  const isLoading = useSelector((state: RootState) => state.wmOfferUrls.urlsByOfferId[offerId]?.isLoading)
  const offer: Offer = useOfferData(offerId);

  const tableCols: TableColumn<WmOfferUrl>[] = [
    {
      name: 'title',
      title: t('main:forms.name'),
      formatter: (value, row) => row.translated_name ?? row.name
    },
    {
      name: 'added_at',
      title: t('table.addedAt'),
      format: 'date'
    },
    {
      name: 'cr',
      title: t('table.cr'),
      formatter: (value, row) => (
        <NumberFormat
          format={FORMAT_PERCENT}
          number={value * 100}
        />
      )
    },
    {
      name: 'ecpc',
      title: t('table.ecpc'),
      format: 'number',
    },
    {
      name: 'actions',
      title: '',
      formatter: (value, row) => (
        <GetLinkButton
          id={offerId}
          getActionLinkUrl={`/publisher/offer/${offerId}/tools/getActionLink?url=${row.id}`}
          buttonProps={{
            size: 'small',
            variant: 'text'
          }}
        />
      )
    }
  ]

  const handleClose = useCallback(() => {
    history.push(history.location.pathname.replace('/getActionLink', ''))
    return false;
  }, [history])

  return (
    <>
      <Typography variant="h6" gutterBottom>{t('landings')} {offer?.translated_name || offer?.name}</Typography>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Typography gutterBottom>{t('landings_total', { count: Object.values(landings).length })}</Typography>
          <Table
            blueTheme
            showRowHover
            cols={tableCols}
            rows={Object.values(landings)}
          />
        </>
      )}


      <GetLinkDialog regexpGetActionLink={/(\/publisher\/offer\/([0-9]{1,}))\/tools\/getActionLink/g} onClose={handleClose} />
    </>
  )

}
